import { inject } from '@angular/core';
import { Route } from '@angular/router';

import { VANILLA_LAZY_ROUTES, VANILLA_SECONDARY_ROUTES } from '@frontend/loaders/routes';
import { HtmlNode, MediaQueryService } from '@frontend/vanilla/core';

import { INTERNAL_ESPORTS_ROUTES } from './esports-web-internal.routes';
import { GtmService } from './main/gtm.service';
import { SportRoute } from './router/multi-route.model';
import { NoMatchRouteGuard } from './router/no-match-route.guard';
import { SeoRedirectGuard } from './seo/seo-redirect.guard';
import { EXTRA_SPORTS_ROUTES, INTERNAL_SPORTS_WEB_ROUTES } from './sports-web-internal.routes';

const configureVanillaLazyRoutes = (routes: Route[]): Route[] => {
    return routes.map((route) => ({
        ...route,
        // Vanilla modules requires MediaQueryService feature, hence we are overriding in sports level
        providers: [{ provide: MediaQueryService }],
        canActivate: [
            () => {
                const htmlNode = inject(HtmlNode);
                htmlNode.setCssClass('vanilla-route', true);
            },
        ],
        canDeactivate: [
            () => {
                const htmlNode = inject(HtmlNode);
                htmlNode.setCssClass('vanilla-route', false);
            },
        ],
    }));
};

export const routes: SportRoute[] = [
    {
        path: '',
        redirectTo: '{culture}/sports',
        pathMatch: 'full',
    },
    {
        path: '{culture}',
        resolve: [() => inject(GtmService).renderGTMTag()],
        children: [
            ...configureVanillaLazyRoutes(VANILLA_LAZY_ROUTES),
            {
                path: '',
                redirectTo: 'sports',
                pathMatch: 'full',
            },
            ...EXTRA_SPORTS_ROUTES,
            {
                path: 'sports',
                children: [...INTERNAL_SPORTS_WEB_ROUTES],
            },
            {
                path: '{esports}',
                children: [...INTERNAL_ESPORTS_ROUTES],
            },
        ],
    },
    /**
     * Configure this route at the root level, not as a child.
     * This means the outlet is always appended to the end of the URL e.g. /en/sports(overlay:menu) or /en/sports/basketball(overlay:menu).
     * This is cleaner and less risky than making the secondary outlet a child of another route e.g. /en/ resulting in /en/(sports//overlay:menu).
     */
    ...configureVanillaLazyRoutes([...VANILLA_SECONDARY_ROUTES]),
    {
        path: '**',
        loadComponent: () => import('./common/empty.component').then((m) => m.EmptyComponent),
        canActivate: [NoMatchRouteGuard, SeoRedirectGuard],
    },
];
